/**
 * Capa de acceso al API público de MANGIBONE (`apps/api`, adba).
 *
 * En dev, `VITE_API_BASE` es `/api` y Vite proxyea a la API (localhost:8087).
 * En prod será la URL pública de la API (p. ej. https://api.mangibone.com/api).
 *
 * Los adaptadores `mapProduct` / `mapRecipe` traducen el shape de la base de
 * datos (name/label/price/...) al shape que usan los componentes de la maqueta.
 */
const API_BASE = (import.meta.env.VITE_API_BASE as string) || "/api";

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok || (json && json.error)) {
    throw new Error((json && json.description) || `HTTP ${res.status}`);
  }
  const payload = json.data ?? json;
  // adba >= 1.0.27 envuelve los listados como { total, data: [...] };
  // las versiones anteriores devolvian el array directo.
  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    Array.isArray((payload as any).data)
  ) {
    return (payload as any).data as T;
  }
  return payload as T;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || (json && json.error)) {
    throw new Error((json && json.description) || `HTTP ${res.status}`);
  }
  return (json.data ?? json) as T;
}

export interface Product {
  slug: string;
  nombre: string;
  categoria: string;
  precio: number;
  unidad: string;
  imagen: string;
  descripcion: string;
  destacado?: boolean;
}

export interface Recipe {
  slug: string;
  titulo: string;
  tiempo_preparacion: string;
  tipo_platillo: string | null;
  porciones: string;
  producto: string;
  imagen: string;
  descripcion?: string;
  ingredientes: string[];
  pasos: string[];
  /** Producto relevante (para agregar al carrito con imagen/precio). */
  productoRef?: Product;
}

/**
 * Bloque editorial (`contents`): hero, products-strip, categories, welcome,
 * recetario. Para el **hero**, `label` lleva el texto/eslogan y
 * `mediaBackground` la imagen de fondo.
 */
export interface Content {
  slug: string;
  label: string;
  category: string;
  background: string;
  excerpt: string;
  content: string;
  link: string;
  pinned: boolean;
  sort: number;
  tags: string;
  time: number;
  transition: string | null;
}

export const mapProduct = (p: any): Product => ({
  slug: p.name,
  nombre: p.label || p.name,
  categoria: p.category || "",
  precio: Number(p.price ?? 0),
  unidad: p.unit || "kg",
  imagen: p.image || "",
  descripcion: p.description || "",
  destacado: !!p.destacado,
});

export const mapRecipe = (r: any): Recipe => ({
  slug: r.name,
  titulo: r.title || r.name,
  tiempo_preparacion: r.prep_time || "",
  tipo_platillo: r.dish_type || null,
  porciones: r.servings || "",
  producto: r.product || "",
  imagen: r.image || "",
  descripcion: r.description || "",
  ingredientes: Array.isArray(r.ingredients) ? r.ingredients : [],
  pasos: Array.isArray(r.steps) ? r.steps : [],
  productoRef: r.product_ref ? mapProduct(r.product_ref) : undefined,
});

export const mapContent = (c: any): Content => ({
  slug: c.name,
  label: c.label || "",
  category: c.category || "",
  background: c.media_background || "",
  excerpt: c.excerpt || "",
  content: c.content || "",
  link: c.link || "",
  pinned: !!c.pinned,
  sort: Number(c.sort ?? 0),
  tags: c.tags || "",
  time: Number(c.time ?? 5000),
  transition: c.transition || null,
});

/** Categoría de producto (`categories`): tarjeta de "Nuestros productos". */
export interface Category {
  slug: string;
  title: string;
  image: string;
  copy: string;
  /** URL asignada a la que navega la tarjeta (ej. /productos?categoria=lacteos). */
  link: string;
}

export const mapCategory = (c: any): Category => ({
  slug: c.name,
  title: c.categoryname || c.name,
  image: c.image || "",
  copy: c.description || "",
  link: c.link || (c.name ? `/productos?categoria=${c.name}` : ""),
});

/** Aviso corto (`notifications`): el texto a mostrar va en `excerpt`. */
export interface Notification {
  slug: string;
  label: string;
  text: string;
  tags: string;
  sort: number;
  time: number;
  transition: string | null;
  link: string;
}

export const mapNotification = (n: any): Notification => ({
  slug: n.name,
  label: n.label || "",
  text: n.excerpt || n.content || "",
  tags: n.tags || "",
  sort: Number(n.sort ?? 0),
  time: Number(n.time ?? 5000),
  transition: n.transition || null,
  link: n.link || "",
});

export const api = {
  listProducts: async (q?: string): Promise<Product[]> => {
    const qs = new URLSearchParams({ limit: "200", "orderBy.position": "asc" });
    if (q) qs.set("q", q);
    return (await get<any[]>(`/products?${qs.toString()}`)).map(mapProduct);
  },
  getProduct: async (slug: string): Promise<Product | null> => {
    try {
      return mapProduct(await get<any>(`/products/${slug}`));
    } catch {
      return null;
    }
  },
  listRecipes: async (q?: string): Promise<Recipe[]> => {
    const qs = new URLSearchParams({ limit: "200", "orderBy.position": "asc" });
    if (q) qs.set("q", q);
    return (await get<any[]>(`/recipes?${qs.toString()}`)).map(mapRecipe);
  },
  getRecipe: async (slug: string): Promise<Recipe | null> => {
    try {
      return mapRecipe(await get<any>(`/recipes/${slug}`));
    } catch {
      return null;
    }
  },
  /** Bloques editoriales (`contents`), opcionalmente filtrados por `category`. */
  listContents: async (category?: string): Promise<Content[]> => {
    const qs = new URLSearchParams({ limit: "200" });
    if (category) qs.set("filters.category", category);
    return (await get<any[]>(`/contents?${qs.toString()}`)).map(mapContent);
  },
  listCategories: async (): Promise<Category[]> =>
    (await get<any[]>("/categories?limit=50&orderBy.position=asc")).map(mapCategory),
  /** Avisos por tag (default `products`): active + pinned, sort DESC, último creado. */
  listNotifications: async (tag = "products"): Promise<Notification[]> => {
    const qs = new URLSearchParams({
      limit: "50",
      "filters.active": "1",
      "filters.pinned": "1",
      "filters.tags": tag,
      "orderBy.sort": "desc",
      "orderBy.created_at": "desc",
    });
    return (await get<any[]>(`/notifications?${qs.toString()}`)).map(mapNotification);
  },
  listSocialLinks: async (): Promise<any[]> => get<any[]>("/social-links?limit=50"),
  listSiteSettings: async (): Promise<any[]> => get<any[]>("/site-settings?limit=100"),
  contact: (data: { name?: string; email: string; phone?: string; message: string }) =>
    post<any>("/contact", { insert: data }),
  /** Envía la compra (el API manda el pedido por WhatsApp al negocio). */
  checkout: (data: {
    items: Array<{ slug: string; nombre: string; precio: number; unidad?: string; qty: number }>;
    phone: string;
    name?: string;
    note?: string;
  }) => post<any>("/cart/checkout", data),
};

export default api;
